import { Component, Input, signal, WritableSignal, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToggleComponent } from '../../../../../shared/components/ui/toggle/toggle.component';
import { LineChartComponent } from '../../../../../shared/components/charts/line-chart/line-chart.component';
import { PaginatedTableComponent, TableHeader } from '../../../../../shared/components/tables/paginated-table/paginated-table.component';
import { ToggleBtnsComponent } from '../../../../../shared/components/toggle-btns/toggle-btns.component';
import { ScrollTableComponent } from '../../../../../shared/components/tables/scrolled-table/scroll-table.component';
import { IndividualToasterComponent } from "../../../../../shared/components/loader/loader.component";
import { IconName } from '../../../../../shared/icons/icon.component';

export interface ToggleOption {
  label: string;
  icon?: string;
  tooltip?: string;
  isTable?: boolean;
  iconName?: IconName;
}

@Component({
  selector: 'app-cards',
  standalone: true,
  imports: [CommonModule, ToggleBtnsComponent, LineChartComponent, ScrollTableComponent, IndividualToasterComponent],
  templateUrl: './cards.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./cards.component.scss'] 
})
export class CardsComponent implements OnInit {
  // Accept toggle options via Input. Provide sensible defaults matching the dummy data.
  @Input() toggleOptions: ToggleOption[] = [
    {
      label: 'Graph',
      icon: 'assets/img/PNG/Vector.png',
      tooltip: 'Click to View Graph',
      isTable: false,
      iconName: 'chartLine' as IconName,
    },
    {
      label: 'Table',
      icon: 'assets/img/PNG/show_table.png',
      tooltip: 'Click to View Table',
      isTable: true,
      iconName: 'betweenHorizontalStart' as IconName,
    },
  ];

  // For tailwind class binding
  @Input() isClassHide: boolean = false;
  @Input() isLoading: boolean = false;
  @Input() classWithoutBorder: boolean = false;

  @Input() showToggle: boolean = true;
  @Input() showLabels: boolean = true; // Control whether toggle button labels are shown
  @Input() defaultView: 'chart' | 'table' = 'chart'; // Default view mode


  // Data passed down to chart or table
  @Input() data: any = null;
  @Input() cardHeading: string = 'ACTIVE BUSINESS';
  @Input() valueKey: string = 'value';
  @Input() curveType: 'curveLinear' | 'curveMonotoneX' | 'curveStep' | 'curveStepBefore' | 'curveStepAfter' = 'curveMonotoneX';
  @Input() unit: string = 'day'; // Unit for date formatting (day, week, month, quarter, year)
  @Input() valueFormat: 'currency' | 'number' | 'percent' = 'number'; // Format type for displaying values

  // Table headers configuration - can be overridden by parent
  @Input() tableHeaders: TableHeader[] = [
    { label: 'Period', key: 'start', align: 'text-left' },
    { label: 'Total', key: 'total', align: 'text-center' },
    { label: 'Dental', key: 'dental', align: 'text-center' },
    { label: 'Healthcare', key: 'healthcare', align: 'text-center' },
    { label: 'Non-Healthcare', key: 'non_healthcare', align: 'text-center' },
  ];

  // Use Angular signals (Angular 19) for local reactive state
  private viewSignal: WritableSignal<'chart' | 'table'> = signal('chart');

  ngOnInit(): void {
    // If toggle is hidden, default to table view
    this.viewSignal.set(this.defaultView);
  }

  // get classes
  get complexClasses(): string {
    if (this.isClassHide) {
      return '';
    }
    if (!this.isClassHide && this.classWithoutBorder) {
      return this.withoutBorderClasses();
    }
    return 'bg-white rounded-lg border border-white/20 backdrop-blur-xl dark:bg-surface-1 dark:border-line shadow-sm transition-colors duration-200';
  }

  withoutBorderClasses(): string {
    return 'bg-white dark:bg-slate-800/40 shadow-sm dark:shadow-slate-900/50 backdrop-blur-md';
  }

  // expose as readonly getter for templates
  get view() {
    return this.viewSignal();
  }

  // Check if previous data exists
  get hasPreviousData(): boolean {
    const previousValue = this.data?.summary?.previous ?? this.data?.previous;
    return previousValue !== undefined && previousValue !== null;
  }

  // Check if all-time data exists
  get hasAllTimeData(): boolean {
    const allTimeValue = this.data?.summary?.allTime ?? this.data?.allTime;
    return allTimeValue !== undefined && allTimeValue !== null;
  }

  // Provide a helper to return tooltip for current selected option
  get currentOption(): ToggleOption | undefined {
    // Determine selected option by view
    return this.toggleOptions.find((o) => !!o.isTable === (this.view === 'table')) || this.toggleOptions[0];
  }

  // Handler wired to the toggle component. Accepts 'graph' | 'table' from ToggleBtnsComponent viewChanged event
  onToggle(event: any) {
    // ToggleBtnsComponent emits ViewMode: 'graph' | 'table'
    if (event === 'table' || event === 'graph') {
      this.viewSignal.set(event === 'table' ? 'table' : 'chart');
      return;
    }

    // Fallback for boolean or object payloads
    let value: boolean | undefined;
    if (typeof event === 'boolean') value = event;
    else if (event && typeof event === 'object') {
      value = (event.value ?? event.detail ?? event.payload ?? event.checked) as any;
      if (typeof value !== 'boolean') {
        if ('isTable' in event) value = !!event.isTable;
        else value = event === 'table' ? true : undefined;
      }
    }

    this.viewSignal.set(value ? 'table' : 'chart');
  }

  // Programmatic setter used by segmented control buttons
  setViewToTable(isTable: boolean) {
    this.viewSignal.set(isTable ? 'table' : 'chart');
  }

  // Format value based on valueFormat input
  formatValue(value: any): string {
    if (value === null || value === undefined) return '-';
    
    const numValue = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(numValue)) return '-';

    switch (this.valueFormat) {
      case 'currency':
        // Format with $ prefix and comma separators, up to 2 decimals
        const formatted = numValue.toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        });
        return `$${formatted}`;
      
      case 'percent':
        return `${numValue.toFixed(1)}%`;
      
      case 'number':
      default:
        return numValue.toLocaleString('en-US');
    }
  }
}
